import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmployeeSchema, UserSummarySchema, type EmployeeDto } from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { ApiError, getData, getPage, patchJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { MigrationTrace } from '../migration/MigrationTrace';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { EmployeeHeaderPicture, EmployeePicture } from './EmployeePicture';
import { EmployeeWorkSection } from './EmployeeWork';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { EmployeeDialog, IDENTITY_AREAS, isMasked, labelOf, useReferences } from './EmployeesPage';
import { EmployeeLeaveSection } from './leave';
import { OnboardingSection } from './onboarding';
import { EmployeeProfileSections, serviceShown } from './profile';
import { businessToday } from '../../shared/documents/dates';
import { EmailLink, PhoneLinks } from '../../shared/ui/contact';

const EMPLOYEES = '/api/v1/hr/employees';

/**
 * One employee (`/hr/employees/:id`; ERD §7.3, FRD-HRP-005 to HRP-013): employment and
 * organization placement, contact details, identity and compliance, the linked system user and
 * the direct reports, with the actions the session may take. Related records link to their own
 * pages only where the session may read them; nothing links to a record it cannot open.
 */
export function EmployeeDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const detail = useQuery({
    queryKey: ['hr', 'employee', id],
    queryFn: () => getData(`${EMPLOYEES}/${id}`, EmployeeSchema),
  });
  const back = (
    <Button component={RouterLink} to="/hr/employees" startIcon={<ArrowBackIcon />}>
      All employees
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the employee" />;
  if (detail.isError) {
    return <RecordFailure title="Employee" noun="employee" error={detail.error} back={back} />;
  }
  return (
    <EmployeeRecord
      employee={detail.data}
      back={back}
      created={params.get('created') === '1'}
      self={false}
    />
  );
}

/**
 * Self-service (`/hr/my-record`; FRD-HRP-021, FRD-USR-023): the signed-in user's own employee
 * record, through the same permission and masking as any other read. The User ↔ Employee link says
 * which record is "own"; it grants nothing by itself, and unmasking still needs its own grant.
 *
 * It is read-only: the FRD lets an employee view their profile (FRD-USR-022, HRP-021) and names no
 * field they may change, so the API refuses a self-service change and nothing here offers one. HR
 * maintains the record from `/hr/employees/:id`.
 */
export function MyEmployeeRecordPage() {
  const own = useQuery({
    queryKey: ['hr', 'employee', 'me'],
    queryFn: () => getData(`${EMPLOYEES}/me`, EmployeeSchema),
  });
  if (own.isPending) return <LoadingState label="Loading your employee record" />;
  if (own.isError) {
    const missing = own.error instanceof ApiError && own.error.status === 404;
    return (
      <Stack spacing={3}>
        <PageHeader title="My employee record" />
        {missing ? (
          <Section>
            <EmptyState
              title="No employee record is linked to your user"
              detail="Ask your administrator to link your user to your employee record."
            />
          </Section>
        ) : (
          <ErrorState message={own.error.message} />
        )}
      </Stack>
    );
  }
  return <EmployeeRecord employee={own.data} back={null} created={false} self />;
}

function EmployeeRecord({
  employee,
  back,
  created,
  self,
}: {
  employee: EmployeeDto;
  back: ReactNode;
  created: boolean;
  self: boolean;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, 'hr.employee:change');
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  const canSeePositions = hasCapability(session.data, 'hr.position:view');
  const references = useReferences();
  const [editing, setEditing] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const status = useMutation({
    mutationFn: () =>
      patchJson(
        `${EMPLOYEES}/${employee.id}`,
        { status: employee.status === 'active' ? 'inactive' : 'active' },
        EmployeeSchema,
        session.data?.csrfToken ?? '',
        employee.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });

  const manager = employee.reportingManagerId;
  // The manager is read on its own, so it is named and linked only if the session may open it.
  const managerRecord = useQuery({
    queryKey: ['hr', 'employee', manager],
    enabled: manager !== null,
    retry: false,
    queryFn: () => getData(`${EMPLOYEES}/${manager ?? ''}`, EmployeeSchema),
  });
  const shown = (value: string | null) =>
    value === null ? '—' : isMasked(value) ? `${value} (masked)` : value;

  return (
    <Stack spacing={3}>
      <PageHeader
        title={self ? 'My employee record' : `${employee.code} — ${employee.name}`}
        leading={<EmployeeHeaderPicture employee={employee} />}
        // Length of service just below the employee code, beside the photo (23 October 2026).
        description={
          self
            ? `${employee.code} — ${employee.name} · Length of service: ${serviceShown(employee, today)}`
            : `Length of service: ${serviceShown(employee, today)}`
        }
        actions={
          <>
            {back}
            {canChange && !self && (
              <Button
                variant="outlined"
                color={employee.status === 'active' ? 'warning' : 'primary'}
                disabled={status.isPending}
                onClick={(event) => {
                  // Leaving asks when and why (CLR-47); returning clears the exit.
                  if (employee.status === 'active') {
                    event.currentTarget.focus();
                    setLeaving(true);
                  } else status.mutate();
                }}
              >
                {employee.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
            {/* Self-service views one's own record; HR maintains it (FRD-HRP-021). */}
            {canChange && !self && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      {created && (
        <Alert severity="success" variant="outlined">
          Employee {employee.code} created. The Employee ID was generated by the system.
        </Alert>
      )}
      {status.isError && <ErrorState message={status.error.message} />}
      {leaving && (
        <ExitDialog
          employee={employee}
          onClose={() => {
            setLeaving(false);
          }}
        />
      )}
      {/* The picture is an employee document; HR adds it here, everyone sees it above (FRD-HRP-071). */}
      <EmployeePicture employee={employee} canAttach={canChange && !self} />
      {/* Staff onboarding (CLR-48): HR starts it here; the employee sees their own. */}
      <OnboardingSection employee={employee} canStart={!self} />
      <Section title="Employment">
        <Facts
          label="Employment details"
          facts={[
            ['Status', <StatusChip key="s" status={employee.status} />],
            ['Employee ID', employee.code],
            ['Name', employee.name],
            // Only an inactive employee has an exit; a masked one reads "••••" either way (CLR-49).
            ...(employee.status !== 'inactive' || employee.exitDate === null
              ? []
              : [
                  [
                    'Exit',
                    isMasked(employee.exitDate) || isMasked(employee.exitReason)
                      ? `${shown(employee.exitDate)}: ${shown(employee.exitReason)}`
                      : `${employee.exitDate}: ${employee.exitReason ?? ''}`,
                  ] satisfies [string, string],
                ]),
            ['Joining date', employee.joiningDate],
            ['Length of service', serviceShown(employee, today)],
            ['Department', labelOf(references.departments, employee.departmentId)],
            ['Cost centre', labelOf(references.costCentres, employee.costCenterId)],
            ['Location', labelOf(references.locations, employee.locationId)],
            [
              'Position',
              employee.positionId === null ? (
                '—'
              ) : canSeePositions ? (
                <RecordLink key="p" to={`/hr/positions/${employee.positionId}`}>
                  {labelOf(references.positions, employee.positionId)}
                </RecordLink>
              ) : (
                'Not visible'
              ),
            ],
            [
              'Reporting manager',
              manager === null ? (
                '—'
              ) : managerRecord.data !== undefined ? (
                <PersonName key="m" name={managerRecord.data.name} employeeId={manager}>
                  <RecordLink to={`/hr/employees/${manager}`}>
                    {managerRecord.data.code} — {managerRecord.data.name}
                  </RecordLink>
                </PersonName>
              ) : managerRecord.isPending ? (
                '…'
              ) : (
                'Not visible'
              ),
            ],
          ]}
        />
      </Section>
      <Section title="Contact">
        <Facts
          label="Contact details"
          facts={[
            ['Email', <EmailLink key="e" email={employee.contactEmail} />],
            ['Phone', <PhoneLinks key="p" phone={employee.contactPhone} />],
            ['Emergency contact', shown(employee.emergencyContactName)],
            ['Emergency phone', <PhoneLinks key="ep" phone={employee.emergencyContactPhone} />],
          ]}
        />
      </Section>
      <Section title="Identity & compliance">
        <Facts
          label="Identity and compliance"
          facts={IDENTITY_AREAS.flatMap(([number, expiry, label]) => [
            [label, shown(employee[number])],
            [`${label.replace(/ number$/, '')} expiry`, shown(employee[expiry])],
          ])}
        />
      </Section>
      <EmployeeWorkSection employeeId={employee.id} canChange={canChange && !self} />
      {/* Profile details, education and the business-card code (FRD-HRP-072 to HRP-079). */}
      <EmployeeProfileSections employee={employee} canChange={canChange && !self} />
      {/* Employee documents follow the record (FRD-HRP-016, HRP-021); self-service only views. */}
      <DocumentsPanel
        recordType="hr.employee"
        recordId={employee.id}
        canAttach={canChange && !self}
      />
      {/* Balances and requests, approved leave highlighted (FRD-HRP-031). */}
      <EmployeeLeaveSection employeeId={employee.id} />
      {!self && <LinkedUser employeeId={employee.id} />}
      {!self && <DirectReports employeeId={employee.id} />}
      {!self && <MigrationTrace recordType="hr.employee" recordId={employee.id} />}
      {!self && <RecordHistory recordType="hr.employee" recordId={employee.id} />}
      {editing && !self && (
        <EmployeeDialog
          key={`${employee.id}:${String(employee.version)}`}
          employee={employee}
          references={references}
          readOnly={!canChange}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * The system user this employee is (FRD-USR-002, FRD-HRP-011), for a session that may read users
 * — user administration is privileged, so an HR user without it sees nothing here rather than a
 * link it could not open.
 */
function LinkedUser({ employeeId }: { employeeId: string }) {
  const session = useSession();
  const canSeeUsers = hasCapability(session.data, 'platform.app_user:view');
  const users = useQuery({
    queryKey: ['platform', 'users', 'by-employee', employeeId],
    enabled: canSeeUsers,
    queryFn: () =>
      getPage(`/api/v1/platform/users?filter[employee_id]=${employeeId}`, UserSummarySchema),
  });
  if (!canSeeUsers) return null;
  const user = users.data?.items[0];
  return (
    <Section title="System user">
      {users.isPending && <LoadingState label="Loading the linked user" />}
      {users.isError && <ErrorState message={users.error.message} />}
      {users.data !== undefined && user === undefined && (
        <EmptyState
          title="No system user is linked"
          detail="A user is linked to an employee from the user's page in Administration."
        />
      )}
      {user !== undefined && (
        <Facts
          label="Linked user"
          facts={[
            [
              'User',
              <PersonName
                key="u"
                name={user.displayName}
                employeeId={user.employeeId ?? employeeId}
                userId={user.id}
              >
                <RecordLink to={`/admin/users/${user.id}`}>
                  {user.displayName} ({user.email})
                </RecordLink>
              </PersonName>,
            ],
            ['Status', <StatusChip key="s" status={user.status} />],
          ]}
        />
      )}
    </Section>
  );
}

/** The employees who report to this one: the other side of the reporting-manager link. */
function DirectReports({ employeeId }: { employeeId: string }) {
  const reports = useQuery({
    queryKey: ['hr', 'employees', 'reports', employeeId],
    queryFn: () =>
      getPage(
        `${EMPLOYEES}?pageSize=200&sort=name&filter[reporting_manager_id]=${employeeId}`,
        EmployeeSchema,
      ),
  });
  return (
    <Section title="Direct reports">
      {reports.isPending && <LoadingState label="Loading direct reports" />}
      {reports.isError && <ErrorState message={reports.error.message} />}
      {reports.data?.items.length === 0 && <EmptyState title="No direct reports" />}
      {reports.data !== undefined && reports.data.items.length > 0 && (
        <Table size="small" aria-label="Direct reports">
          <TableHead>
            <TableRow>
              <TableCell>Employee ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.data.items.map((report) => (
              <TableRow key={report.id}>
                <TableCell>
                  <RecordLink to={`/hr/employees/${report.id}`}>{report.code}</RecordLink>
                </TableCell>
                <TableCell>
                  <PersonName name={report.name} employeeId={report.id} />
                </TableCell>
                <TableCell>
                  <StatusChip status={report.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

/**
 * Making an employee inactive records when and why they left (CLR-47, FRD-HRP-098): the exit
 * counts in the exits and turnover figures of the dashboards. Both are required here.
 */
function ExitDialog({ employee, onClose }: { employee: EmployeeDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [exitDate, setExitDate] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${EMPLOYEES}/${employee.id}`,
        { status: 'inactive', exitDate, exitReason: reason.trim() },
        EmployeeSchema,
        session.data?.csrfToken ?? '',
        employee.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
      onClose();
    },
  });
  const beforeJoining = exitDate !== '' && exitDate < employee.joiningDate;
  return (
    <FormDialog
      open
      title={`Deactivate ${employee.code}`}
      submitLabel="Deactivate"
      canSubmit={exitDate !== '' && !beforeJoining && reason.trim() !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Alert severity="info" variant="outlined">
        The exit date and reason are counted in the exits and turnover figures of the dashboards.
      </Alert>
      <TextField
        required
        type="date"
        label="Exit date"
        value={exitDate}
        error={beforeJoining}
        helperText={beforeJoining ? 'The exit date cannot be before the joining date' : undefined}
        onChange={(e) => {
          setExitDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        required
        label="Reason"
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 500 } }}
      />
    </FormDialog>
  );
}
